import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import TeamAttendanceView from '../components/TeamAttendanceView';
import { toast } from 'sonner';
import { format, getDaysInMonth, startOfMonth, getDay } from 'date-fns';
import { CheckCircle2, XCircle, Clock, CalendarDays, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AttendancePage = ({ user, setUser }) => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [attendance, setAttendance] = useState([]);
  const [leaveCalendar, setLeaveCalendar] = useState({}); // Leave dates for calendar
  const [stats, setStats] = useState({});
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingChanges, setPendingChanges] = useState({}); // Store unsaved attendance selections
  const [submitting, setSubmitting] = useState(false);
  // Joining date guard — calendar cells before this date are shown as
  // greyed-out / non-actionable (employee wasn't on the rolls yet).
  // Prefers ``actual_date_of_joining`` (the date the employee actually started,
  // which can differ from the offered/contractual ``date_of_joining``) and
  // falls back to ``date_of_joining`` when the actual one isn't recorded.
  const [joiningDate, setJoiningDate] = useState(null);
  // If the current user manages at least one other employee we surface a
  // second tab that lets them view their whole team's attendance in one
  // grid. Non-managers see the original single-user calendar view exactly
  // as before — no visual change, no extra API call for them.
  const [isManager, setIsManager] = useState(false);
  const [activeTab, setActiveTab] = useState('mine');

  // Year dropdown options: dynamic from the employee's joining year through
  // the current year (latest first). Falls back to just the current year
  // while ``joiningDate`` is still loading. Replaces the old hard-coded
  // ``[2024, 2025, 2026]`` so employees with earlier DOJs can browse their
  // full history, and the list doesn't go stale at year-end.
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    let startYear = currentYear;
    if (joiningDate) {
      const parsed = parseInt(String(joiningDate).slice(0, 4), 10);
      if (parsed && parsed <= currentYear) startYear = parsed;
    }
    const years = [];
    for (let y = currentYear; y >= startYear; y--) years.push(y);
    return years;
  }, [joiningDate]);

  // If the currently-selected year is no longer in the dropdown's valid
  // range (e.g. joiningDate just loaded and is later than the previously
  // selected year), snap to the most recent valid year.
  useEffect(() => {
    if (yearOptions.length > 0 && !yearOptions.includes(selectedYear)) {
      setSelectedYear(yearOptions[0]);
    }
  }, [yearOptions, selectedYear]);

  const statusOptions = ['P', 'UP', 'AL', 'AHD', 'UPHD', 'OH', 'RH', 'WO'];
  
  // Colors for attendance status
  const statusColors = {
    P: 'bg-green-500',
    UP: 'bg-red-500',
    AL: 'bg-blue-500',        // Annual Leave - Blue when approved
    AHD: 'bg-green-300',
    UPHD: 'bg-red-300',
    OH: 'bg-orange-500',      // Organization Holiday - Orange
    RH: 'bg-purple-500',      // Restricted Holiday - Purple
    WO: 'bg-gray-400',
    LWP: 'bg-red-600'         // Leave Without Pay - Dark Red
  };

  // Colors for leave types (used in calendar)
  const leaveColors = {
    // Pending leaves - Amber
    pending: {
      AL: 'bg-amber-400 border-amber-600',
      OH: 'bg-amber-400 border-amber-600',
      RH: 'bg-amber-400 border-amber-600',
      LWP: 'bg-amber-400 border-amber-600'
    },
    // Approved leaves - Type specific colors
    approved: {
      AL: 'bg-blue-500 border-blue-700',      // Annual Leave - Blue
      OH: 'bg-orange-500 border-orange-700',   // Organization Holiday - Orange
      RH: 'bg-purple-500 border-purple-700',   // Restricted Holiday - Purple
      LWP: 'bg-red-600 border-red-800'        // Leave Without Pay - Dark Red
    },
    // Rejected leaves - Gray
    rejected: {
      AL: 'bg-gray-300 border-gray-500',
      OH: 'bg-gray-300 border-gray-500',
      RH: 'bg-gray-300 border-gray-500',
      LWP: 'bg-gray-300 border-gray-500'
    }
  };

  const statusLabels = {
    P: 'Present',
    UP: 'Unpaid',
    AL: 'Annual Leave',
    AHD: 'Approved Half Day',
    UPHD: 'Unpaid Half Day',
    OH: 'Organization Holiday',
    RH: 'Restricted Holiday',
    WO: 'Week Off',
    LWP: 'Leave Without Pay'
  };

  const leaveTypeLabels = {
    AL: 'Annual Leave',
    OH: 'Organizational Holiday',
    RH: 'Restricted Holiday',
    LWP: 'Leave Without Pay'
  };

  useEffect(() => {
    fetchAttendance();
    fetchLeaveCalendar();
    if (user.role === 'manager' || user.role === 'admin') {
      fetchPendingApprovals();
    }
  }, [selectedMonth, selectedYear]);

  // Joining date is a per-user attribute, so fetch it once on mount (not on
  // every month/year change). Falls back silently if the details endpoint
  // fails — the calendar just won't grey out pre-joining cells.
  useEffect(() => {
    const fetchJoining = async () => {
      try {
        const token = localStorage.getItem('token');
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const res = await axios.get(`${API}/users/${user.id}/details`, config);
        const doj = res.data?.actual_date_of_joining || res.data?.date_of_joining || null;
        setJoiningDate(doj);
      } catch (e) {
        // Silent — pre-joining grey-out is non-critical.
      }
    };
    if (user?.id) fetchJoining();
  }, [user?.id]);

  // Detect whether the current user is a Reporting Manager (or admin) —
  // used to conditionally render the "View Team Attendance" tab.
  useEffect(() => {
    const checkRm = async () => {
      if (user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'manager') {
        setIsManager(true);
        return;
      }
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/my-approvals/check-rm`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setIsManager(!!res.data?.is_rm);
      } catch (e) {
        // Silent — worst case they just don't see the tab.
      }
    };
    if (user?.id) checkRm();
  }, [user?.id, user?.role]);

  const fetchAttendance = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/attendance?month=${selectedMonth}&year=${selectedYear}`, config);
      setAttendance(response.data);
      calculateStats(response.data);
    } catch (error) {
      toast.error('Failed to load attendance');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaveCalendar = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/leaves/calendar?month=${selectedMonth}&year=${selectedYear}`, config);
      setLeaveCalendar(response.data);
    } catch (error) {
      console.error('Failed to load leave calendar:', error);
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/attendance/pending`, config);
      setPendingApprovals(response.data);
    } catch (error) {
      console.error('Failed to load pending approvals');
    }
  };

  const calculateStats = (data) => {
    const counts = {};
    statusOptions.forEach(status => counts[status] = 0);
    counts['LWP'] = 0; // Add LWP to stats
    data.filter(a => a.approval_status === 'approved').forEach(record => {
      if (counts[record.status] !== undefined) {
        counts[record.status]++;
      }
    });
    setStats(counts);
  };

  const handleStatusChange = (date, status) => {
    // Store the change instead of immediately submitting
    setPendingChanges(prev => ({
      ...prev,
      [date]: status
    }));
  };

  const handleSubmitAttendance = async () => {
    if (Object.keys(pendingChanges).length === 0) {
      toast.info('No attendance changes to submit');
      return;
    }

    setSubmitting(true);
    const token = localStorage.getItem('token');
    const config = { headers: { Authorization: `Bearer ${token}` } };
    
    let successCount = 0;
    let failCount = 0;

    try {
      // Submit all pending changes
      const submissions = Object.entries(pendingChanges).map(([date, status]) => 
        axios.post(`${API}/attendance`, { date, status }, config)
          .then(() => successCount++)
          .catch(() => failCount++)
      );

      await Promise.all(submissions);

      if (successCount > 0) {
        toast.success(`${successCount} attendance record(s) submitted for approval`);
      }
      if (failCount > 0) {
        toast.error(`${failCount} submission(s) failed`);
      }

      // Clear pending changes and refresh
      setPendingChanges({});
      fetchAttendance();
      fetchLeaveCalendar();
    } catch (error) {
      toast.error('Failed to submit attendance');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproval = async (attendanceId, approvalStatus) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/attendance/approve`, { attendance_id: attendanceId, approval_status: approvalStatus }, config);
      toast.success(`Attendance ${approvalStatus}`);
      fetchPendingApprovals();
    } catch (error) {
      toast.error('Failed to process approval');
    }
  };

  const isDateAllowed = (day) => {
    const date = new Date(selectedYear, selectedMonth - 1, day);
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);
    return date <= today && date >= sevenDaysAgo;
  };

  // Returns true when the calendar day falls strictly before the employee's
  // joining date — those cells should render as greyed-out and non-actionable
  // (no Select, no leave/attendance actions) because the employee was not on
  // the rolls yet.
  const isBeforeJoining = (day) => {
    if (!joiningDate) return false;
    const joinParts = String(joiningDate).slice(0, 10).split('-');
    if (joinParts.length !== 3) return false;
    const [jy, jm, jd] = joinParts.map(Number);
    if (!jy || !jm || !jd) return false;
    // Compare as integer YYYYMMDD to sidestep timezone drift.
    const cell = selectedYear * 10000 + selectedMonth * 100 + day;
    const join = jy * 10000 + jm * 100 + jd;
    return cell < join;
  };

  const getAttendanceForDate = (day) => {
    const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return attendance.find(a => a.date === dateStr);
  };

  const getLeaveForDate = (day) => {
    const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return leaveCalendar[dateStr] || null;
  };

  const getLeaveColorClass = (leave) => {
    if (!leave) return '';
    const status = leave.approval_status;
    const type = leave.leave_type;
    // Approved half-day AL -> AHD styling
    if (status === 'approved' && leave.leave_category === 'half_day' && type === 'AL') {
      return 'bg-green-400 border-green-600';
    }
    return leaveColors[status]?.[type] || 'bg-gray-200';
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(new Date(selectedYear, selectedMonth - 1));
    const firstDay = getDay(startOfMonth(new Date(selectedYear, selectedMonth - 1)));
    const days = [];

    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="aspect-square"></div>);
    }

    // Actual days
    for (let day = 1; day <= daysInMonth; day++) {
      // Pre-joining short-circuit: render a flat greyed-out cell with no
      // attendance Select, no leave badge, and a small "Before joining" hint.
      // The user can still see the day number for context but cannot interact.
      if (isBeforeJoining(day)) {
        days.push(
          <div
            key={day}
            className="aspect-square border rounded-lg p-2 bg-gray-100 opacity-60 cursor-not-allowed"
            data-testid={`calendar-day-${day}`}
            title="Before your joining date"
          >
            <div className="text-sm font-medium mb-1 text-gray-500">{day}</div>
            <div
              className="text-[10px] text-gray-500 leading-tight"
              data-testid={`before-joining-${day}`}
            >
              Before joining
            </div>
          </div>
        );
        continue;
      }

      const record = getAttendanceForDate(day);
      const leave = getLeaveForDate(day);
      const isAllowed = isDateAllowed(day);
      const isApproved = record?.approval_status === 'approved';
      const isPending = record?.approval_status === 'pending';
      const isRejected = record?.approval_status === 'rejected';
      // Allow re-selection on the calendar whenever the day is within the
      // 7-day window OR the user is resubmitting a previously rejected entry
      // (the backend accepts the latter regardless of age).
      const canEdit = (isAllowed || isRejected) && !isApproved;
      const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const hasPendingChange = pendingChanges.hasOwnProperty(dateStr);
      const displayValue = hasPendingChange ? pendingChanges[dateStr] : (record?.status || '');
      
      // Determine if this date has a leave
      const hasLeave = leave !== null;
      const isLeaveApproved = leave?.approval_status === 'approved';
      const isLeavePending = leave?.approval_status === 'pending';
      const isLeaveRejected = leave?.approval_status === 'rejected';
      const isLeaveCancelled = leave?.approval_status === 'cancelled';
      // A rejected OR cancelled leave should NOT block attendance entry for
      // that day. From the calendar's perspective, the day behaves as if no
      // leave was filed (cancelled = user voluntarily withdrew; rejected =
      // manager refused). The backend now also filters cancelled rows out
      // of ``/leaves/calendar``, but we keep this defensive check so a stale
      // cached response can't reintroduce the bug.
      const effectiveHasLeave = hasLeave && !isLeaveRejected && !isLeaveCancelled;

      // Determine cell background based on leave status (rejected leaves
      // intentionally fall through to the white default).
      let cellBgClass = 'bg-white';
      if (effectiveHasLeave) {
        if (isLeavePending) {
          cellBgClass = 'bg-amber-50 border-amber-400 border-2';
        } else if (isLeaveApproved) {
          const isHalfDay = leave.leave_category === 'half_day';
          if (isHalfDay) {
            cellBgClass = 'bg-green-50 border-green-400 border-2';
          } else if (leave.leave_type === 'LWP') {
            cellBgClass = 'bg-red-50 border-red-400 border-2';
          } else if (leave.leave_type === 'OH') {
            cellBgClass = 'bg-orange-50 border-orange-400 border-2';
          } else if (leave.leave_type === 'RH') {
            cellBgClass = 'bg-purple-50 border-purple-400 border-2';
          } else {
            cellBgClass = 'bg-blue-50 border-blue-400 border-2';
          }
        }
      }

      days.push(
        <div
          key={day}
          className={`aspect-square border rounded-lg p-2 ${!isAllowed && !effectiveHasLeave ? 'bg-gray-100' : hasPendingChange ? 'bg-yellow-50 border-yellow-400' : cellBgClass} ${effectiveHasLeave ? '' : 'hover:shadow-md'} transition-shadow`}
          data-testid={`calendar-day-${day}`}
        >
          <div className="text-sm font-medium mb-1">{day}</div>
          
          {/* Show leave badge only for non-rejected leaves */}
          {effectiveHasLeave && (
            <div 
              className={`${getLeaveColorClass(leave)} text-white text-xs px-2 py-1 rounded text-center font-medium border`}
              title={`${leave.leave_category === 'half_day' && leave.approval_status === 'approved' && leave.leave_type === 'AL' ? 'Approved Half Day' : leaveTypeLabels[leave.leave_type]} - ${leave.approval_status}`}
            >
              {leave.leave_category === 'half_day' && leave.approval_status === 'approved' && leave.leave_type === 'AL' ? 'AHD' : leave.leave_type}
              {isLeavePending && <Clock className="w-3 h-3 inline ml-1" title="Pending Approval" />}
              {isLeaveApproved && <CheckCircle2 className="w-3 h-3 inline ml-1" title="Approved" />}
            </div>
          )}

          {/* Inline note if the leave on this day was rejected so the
              employee knows the day is once again free for attendance entry. */}
          {hasLeave && isLeaveRejected && (
            <div
              className="text-[10px] text-red-600 leading-tight mb-1"
              title={leave?.rejection_reason || `Rejected ${leaveTypeLabels[leave.leave_type] || ''}`}
              data-testid={`leave-rejected-hint-${day}`}
            >
              {leaveTypeLabels[leave.leave_type] || leave.leave_type} rejected — you can mark attendance instead
              {leave?.rejection_reason && (
                <div className="italic">&quot;{leave.rejection_reason}&quot;</div>
              )}
            </div>
          )}

          {/* Show attendance selector if no leave (or leave was rejected) */}
          {!effectiveHasLeave && canEdit ? (
            <>
              <Select
                value={displayValue}
                onValueChange={(value) => handleStatusChange(dateStr, value)}
                disabled={isPending}
              >
                <SelectTrigger className="h-8 text-xs" data-testid={`status-select-${day}`}>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map(status => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isRejected && (
                <div className="mt-1 text-[10px] text-red-600 leading-tight" data-testid={`rejected-hint-${day}`}>
                  Rejected — pick again &amp; resubmit
                  {record?.rejection_reason && (
                    <div
                      className="italic mt-0.5 max-h-12 overflow-hidden"
                      title={record.rejection_reason}
                      data-testid={`rejection-reason-${day}`}
                    >
                      &quot;{record.rejection_reason}&quot;
                    </div>
                  )}
                </div>
              )}
            </>
          ) : !effectiveHasLeave && record ? (
            <div className={`${statusColors[record.status]} text-white text-xs px-2 py-1 rounded text-center font-medium`} data-testid={`status-badge-${day}`}>
              {record.status}
              {isPending && <Clock className="w-3 h-3 inline ml-1" />}
              {isRejected && <XCircle className="w-3 h-3 inline ml-1" />}
            </div>
          ) : null}
          
          {hasPendingChange && !isApproved && !effectiveHasLeave && (
            <div className="text-xs text-yellow-600 mt-1">●</div>
          )}
        </div>
      );
    }

    return days;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50" data-testid="attendance-page">
      <Header user={user} setUser={setUser} />
      <Navigation user={user} />
      
      <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-screen-2xl mx-auto w-full">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Attendance Management</h1>
          <p className="text-gray-600">Mark and track your attendance</p>
        </div>

        {/* Manager Approvals */}
        {(user.role === 'manager' || user.role === 'admin') && pendingApprovals.length > 0 && (
          <Card className="mb-6" data-testid="pending-approvals-section">
            <CardHeader>
              <CardTitle>Pending Approvals ({pendingApprovals.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingApprovals.map(record => (
                  <div key={record.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg" data-testid={`approval-item-${record.id}`}>
                    <div>
                      <p className="font-medium">{record.employee_name}</p>
                      <p className="text-sm text-gray-600">{record.date} - {statusLabels[record.status]}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleApproval(record.id, 'approved')} className="bg-green-600 hover:bg-green-700" data-testid={`approve-${record.id}`}>
                        <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleApproval(record.id, 'rejected')} className="border-red-600 text-red-600 hover:bg-red-50" data-testid={`reject-${record.id}`}>
                        <XCircle className="w-4 h-4 mr-1" /> Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {isManager && (
            <TabsList className="mb-4" data-testid="attendance-tabs">
              <TabsTrigger value="mine" data-testid="tab-my-attendance">
                <CalendarDays className="w-4 h-4 mr-2" /> My Attendance
              </TabsTrigger>
              <TabsTrigger value="team" data-testid="tab-view-team-attendance">
                <Users className="w-4 h-4 mr-2" /> View Team Attendance
              </TabsTrigger>
            </TabsList>
          )}
          <TabsContent value="mine" className="mt-0">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Calendar */}
          <Card className="lg:col-span-3">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Attendance Calendar</CardTitle>
                <div className="flex gap-2">
                  <Select value={String(selectedMonth)} onValueChange={(value) => setSelectedMonth(Number(value))}>
                    <SelectTrigger className="w-32" data-testid="month-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 12 }, (_, i) => (
                        <SelectItem key={i + 1} value={String(i + 1)}>
                          {format(new Date(2024, i, 1), 'MMMM')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={String(selectedYear)} onValueChange={(value) => setSelectedYear(Number(value))}>
                    <SelectTrigger className="w-28" data-testid="year-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {yearOptions.map(year => (
                        <SelectItem key={year} value={String(year)} data-testid={`year-option-${year}`}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-2 mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-center text-sm font-medium text-gray-600">{day}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-2">
                {renderCalendar()}
              </div>
              <div className="mt-4 text-sm text-gray-600">
                <p>Note: You can submit attendance for today and up to 7 days back</p>
                {Object.keys(pendingChanges).length > 0 && (
                  <p className="text-yellow-600 font-semibold mt-2">
                    ● {Object.keys(pendingChanges).length} unsaved change(s) - Click Submit button below
                  </p>
                )}
              </div>
              {Object.keys(pendingChanges).length > 0 && (
                <div className="mt-4 flex gap-3">
                  <Button 
                    onClick={handleSubmitAttendance}
                    disabled={submitting}
                    className="bg-green-600 hover:bg-green-700 flex-1"
                    data-testid="submit-attendance-button"
                  >
                    {submitting ? 'Submitting...' : `Submit ${Object.keys(pendingChanges).length} Attendance Record(s)`}
                  </Button>
                  <Button 
                    onClick={() => setPendingChanges({})}
                    variant="outline"
                    disabled={submitting}
                  >
                    Clear All
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Statistics & Legend */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Monthly Stats</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(statusLabels).map(([key, label]) => (
                    <div key={key} className="flex items-center justify-between" data-testid={`stat-${key}`}>
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded ${statusColors[key]}`}></div>
                        <span className="text-sm">{label}</span>
                      </div>
                      <span className="font-semibold">{stats[key] || 0}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Leave Legend */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CalendarDays className="w-5 h-5" />
                  Leave Legend
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-amber-400 border border-amber-600"></div>
                    <span className="text-sm">Pending Leave</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-blue-500 border border-blue-700"></div>
                    <span className="text-sm">Annual Leave (Approved)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-orange-500 border border-orange-700"></div>
                    <span className="text-sm">Organizational Holiday (Approved)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-purple-500 border border-purple-700"></div>
                    <span className="text-sm">Restricted Holiday (Approved)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-red-600 border border-red-800"></div>
                    <span className="text-sm">LWP (Approved)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-gray-300 border border-gray-500 border-dashed"></div>
                    <span className="text-sm">Rejected Leave</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
          </TabsContent>
          {isManager && (
            <TabsContent value="team" className="mt-0">
              <TeamAttendanceView user={user} />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default AttendancePage;
